import React, { useEffect, useRef } from "react";

const FEEDBACK_KEYFRAMES_CSS = `
@keyframes input-feedback-shake {
    0%   { transform: translateX(0); }
    25%  { transform: translateX(-2px); }
    50%  { transform: translateX(2px); }
    75%  { transform: translateX(-2px); }
    100% { transform: translateX(0); }
}
@keyframes input-feedback-warning-flash {
    0%   { color: rgb(var(--color-warning-base)); }
    11%  { color: rgb(var(--color-warning-base)); }
    100% { color: rgb(var(--color-foreground-base)); }
}
@keyframes input-feedback-warning-border {
    0%   { box-shadow: inset 0 0 0 1px rgb(var(--color-warning-base)); }
    11%  { box-shadow: inset 0 0 0 1px rgb(var(--color-warning-base)); }
    100% { box-shadow: inset 0 0 0 1px rgb(var(--color-warning-base) / 0); }
}
@keyframes input-feedback-error-flash {
    0%   { color: rgb(var(--color-error-base)); }
    11%  { color: rgb(var(--color-error-base)); }
    100% { color: rgb(var(--color-foreground-base)); }
}
@keyframes input-feedback-error-border {
    0%   { box-shadow: inset 0 0 0 1px rgb(var(--color-error-base)); }
    11%  { box-shadow: inset 0 0 0 1px rgb(var(--color-error-base)); }
    100% { box-shadow: inset 0 0 0 1px rgb(var(--color-error-base) / 0); }
}
`;

type FeedbackTone = "warning" | "error";

interface InputFeedbackProps {
    /**
     * Increment to fire a one-shot animation. Each increment restarts the
     * animation imperatively (without remounting children, so focus + cursor
     * position survive a rejection mid-typing).
     */
    trigger: number;
    /** Tone — `warning` (yellow) for soft caps, `error` (red) for hard
     *  failures. Defaults to `warning`. */
    tone?: FeedbackTone;
    /**
     * CSS selector (relative to the wrapper) for the descendant that should
     * receive the color-flash animation. The shake + border animations are
     * always applied to the wrapper itself. Defaults to `"input"` so the
     * `<input>` text flashes alongside the wrapper's shake.
     */
    flashSelector?: string;
    /** Optional className passed to the wrapper. */
    className?: string;
    children: React.ReactNode;
}

/**
 * Pulse-style feedback for inputs. Wraps a bordered input shell, and on each
 * `trigger` increment fires a coordinated shake + warning-tinted border flash
 * on the wrapper plus a color-flash on the inner `<input>` (or any
 * `flashSelector`-matching element).
 *
 * Today: used by `AmountInput` when the sanitizer rejects a digit (integer
 * or decimal cap).
 *
 * Tomorrow: any input that wants the same "you tried to do something we
 * won't accept" tactile feedback. Switch `tone="error"` for red treatment
 * (e.g., a server-side validation failure).
 *
 * Implementation note: animations are applied via `style.animation` resets
 * on a ref instead of `key=` remounts, so the wrapped input keeps focus +
 * cursor position when the user hits a cap mid-typing.
 */
export function InputFeedback({
    trigger,
    tone = "warning",
    flashSelector = "input",
    className,
    children
}: InputFeedbackProps): React.ReactElement {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (trigger === 0) return;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        wrapper.style.animation = "none";
        void wrapper.offsetWidth;
        wrapper.style.animation = `input-feedback-shake 200ms ease-out, input-feedback-${tone}-border 700ms ease-out`;

        const flashEl = wrapper.querySelector(flashSelector);
        if (flashEl instanceof HTMLElement) {
            flashEl.style.animation = "none";
            void flashEl.offsetWidth;
            flashEl.style.animation = `input-feedback-${tone}-flash 700ms ease-out`;
        }
    }, [trigger, tone, flashSelector]);

    return (
        <>
            <style
                dangerouslySetInnerHTML={{ __html: FEEDBACK_KEYFRAMES_CSS }}
            />
            <div ref={wrapperRef} className={className}>
                {children}
            </div>
        </>
    );
}
